import axios from 'axios';
import { fetchBuyer, fetchSupplier, getAccountingClasses, getAccountingConfiguration, getAccountingInfo, getAccountingVendorsData, getCatalogProductForSku, getGLCodesData, getTaxCodesData } from './OcrNetwork';
import { TAX_CODE_OTHER_EXEMPT, } from '../types/OcrTypes';
import { getLineItemsFromInvoiceData, getValidUnit, valueToFixedNumber } from '../logic/OcrInvoiceHelpers';
import { getCurrentLocalDateAsUtcIso, parseDateToIsoString } from '../utils/DateUtils';
import { getEmptyInvoice, getEmptyLineItem } from '../logic/OcrInvoiceReducer';
import { LineItemSchema, LineItemSchemaWithGlcodeIDRequired } from '../logic/OcrInvoiceValidator';
import { OCR_API_ENDPOINTS } from "./types/OcrApiEndpoints";
export var TaxCalculationSettings;
(function (TaxCalculationSettings) {
    TaxCalculationSettings["ACCOUNTING"] = "ACCOUNTING";
    TaxCalculationSettings["INCLUSIVE_OF_TAX"] = "INCLUSIVE_OF_TAX";
    TaxCalculationSettings["CUSTOM"] = "CUSTOM";
})(TaxCalculationSettings || (TaxCalculationSettings = {}));
export async function loadOcrData(invoiceKey, platform, accessToken = '') {
    var _a;
    // TODO: what is the reason to have empthy invoice if we will overwrite it later?
    let finalInvoice = getEmptyInvoice();
    const result = {
        invoiceData: null,
        catalogPriceListID: '',
        catalogID: '',
        accountingConfiguration: null,
        accountingInfo: null,
        glCodes: [],
        taxCodes: [],
        accountingClasses: [],
        accountingVendors: null,
        accountingVendorID: '',
        buyerName: '',
        supplierName: '',
        isAccountingEnabled: false,
    };
    try {
        // stage 1
        const axiosResponse = await axios.get(`${OCR_API_ENDPOINTS.OCR_API}/invoices/${invoiceKey}`);
        const invoiceData = axiosResponse.data;
        result.invoiceData = invoiceData;
        result.buyerName = invoiceData.buyerName;
        result.supplierName = invoiceData.supplierName;
        await getAccountingConfiguration(invoiceData.buyerUrlsafeKey).then((accountingConfiguration) => {
            result.accountingConfiguration = accountingConfiguration;
            result.isAccountingEnabled = accountingConfiguration === null || accountingConfiguration === void 0 ? void 0 : accountingConfiguration.isEnabled;
        }).catch((err) => {
            console.error(err);
        });
        if (result.isAccountingEnabled) {
            // Avoid QBO invalid_grant issue by calling these one at a time
            await getAccountingInfo(platform, invoiceData.buyerUrlsafeKey, accessToken).then((accountingInfo) => {
                result.accountingInfo = accountingInfo;
            }).catch((err) => {
                console.error(err);
            });
            await getGLCodesData(platform, invoiceData.buyerUrlsafeKey, accessToken).then((glcodeResult) => {
                result.glCodes = glcodeResult !== null && glcodeResult !== void 0 ? glcodeResult : [];
            }).catch((err) => {
                console.error(err);
            });
            await getTaxCodesData(platform, invoiceData.buyerUrlsafeKey, accessToken).then((taxCodesResult) => {
                var _a;
                result.taxCodes = (_a = taxCodesResult.taxCodes) !== null && _a !== void 0 ? _a : [];
            }).catch((err) => {
                console.error(err);
            });
            await getAccountingClasses(platform, invoiceData.buyerUrlsafeKey, accessToken).then((glcodeClasses) => {
                result.accountingClasses = glcodeClasses === null || glcodeClasses === void 0 ? void 0 : glcodeClasses.classes;
            }).catch((err) => {
                console.error(err);
            });
        }
        // stage 2 promises
        const stage2Promises = [];
        // console.log(invoiceData?.orderUrlsafeKey, 'invoiceData?.orderUrlsafeKey ')
        // if(invoiceData?.orderUrlsafeKey){
        //     stage2Promises.push(getOrderData(invoiceData.orderUrlsafeKey).then((orderData) => {
        //         console.log(orderData, 'orderData ')
        //     }).catch((err) => {
        //         console.log('Error retrieving order data', err);
        //     }))
        // }
        stage2Promises.push(getOcrCatalogData(invoiceData.buyerUrlsafeKey).then((value) => {
            const parsedCatalogData = parseCatalogData(value.supplierCatalogs, invoiceData.supplierUrlsafeKey);
            if (parseCatalogData) {
                result.catalogID = parsedCatalogData.catalogID;
                result.catalogPriceListID = parsedCatalogData.priceListID;
            }
        }));
        stage2Promises.push(fetchBuyer(invoiceData.buyerUrlsafeKey).then((buyer) => {
            result.buyer = buyer;
        }));
        stage2Promises.push(fetchSupplier(invoiceData === null || invoiceData === void 0 ? void 0 : invoiceData.supplierUrlsafeKey, invoiceData.buyerUrlsafeKey).then((supplier) => {
            result.supplier = supplier.data;
        }));
        if (result.isAccountingEnabled) {
            stage2Promises.push(getAccountingVendorsData(platform, invoiceData.buyerUrlsafeKey, accessToken).then((value) => {
                result.accountingVendors = value === null || value === void 0 ? void 0 : value.vendors;
            }).catch((err) => {
                console.error(err);
            }));
        }
        try {
            await Promise.allSettled(stage2Promises).catch((err) => {
                console.log(err);
            });
        }
        catch (_b) {
            // TODO: this catch will not be triggered never, we have catch on Promise.allSettled above
            // Accounting routes are sending back not founds as 500 server errors which breaks everything so just ignore this crap
            console.log('crap failed, ignoring it');
        }
        // finally do a big search for SKUs
        finalInvoice = transformNetworkData(result);
        finalInvoice.rawInvoiceData = invoiceData;
        finalInvoice.glCodes = result.glCodes;
        finalInvoice.accountingClasses = result.accountingClasses;
        finalInvoice.invoiceID = invoiceKey;
        finalInvoice.accountingVendors = result.accountingVendors;
        finalInvoice.lineItems = await fetchPackageForLineItems(finalInvoice.lineItems, result.catalogID, result.catalogPriceListID);
        finalInvoice.lineItems = finalInvoice.lineItems.filter((i) => i.isRemoved !== true);
        if (result.isAccountingEnabled) {
            const { taxCode, taxCodeNonTaxable } = (_a = result.accountingConfiguration) === null || _a === void 0 ? void 0 : _a.data;
            const omitTaxCodeID = finalInvoice.topLevelData.taxCalculationSettings === TaxCalculationSettings.CUSTOM;
            finalInvoice.lineItems = finalInvoice.lineItems.map((i) => {
                if (omitTaxCodeID) {
                    i.primaryItemData.taxCodeID = undefined;
                }
                else if (!i.primaryItemData.taxCodeID) {
                    const taxCodeObject = i.primaryItemData.tax.dollarAmount > 0 ? taxCode : taxCodeNonTaxable;
                    i.primaryItemData.taxCodeID = taxCodeObject === null || taxCodeObject === void 0 ? void 0 : taxCodeObject.id;
                }
                return i;
            });
        }
        finalInvoice.lowConfidenceFields = lowConfidenceFieldsInLineItems(finalInvoice.lineItems);
        finalInvoice.missingRequiredFields = missingRequiredFieldsInLineItems(finalInvoice.lineItems, finalInvoice.isQBOEnabled);
        return finalInvoice;
    }
    catch (errors) {
        console.log(errors, 'errors');
    }
    return finalInvoice;
}
const LOW_CONFIDENCE_THRESHOLD = 0.8;
function missingRequiredFieldsInLineItems(lineItems, isAccountingEnabled = false) {
    const lineItemFields = lineItems.map(lineItem => lineItem.primaryItemData);
    const missingFields = lineItemFields.map((lineItem) => {
        var _a, _b;
        const validationSchema = isAccountingEnabled ? LineItemSchemaWithGlcodeIDRequired : LineItemSchema;
        const parseLineItem = validationSchema.safeParse(lineItem);
        if (parseLineItem.success === false) {
            const fieldsWithErrors = (_a = parseLineItem.error) === null || _a === void 0 ? void 0 : _a.issues.map(issue => issue.path[0]);
            const uniqueFieldsWithErrors = [...new Set(fieldsWithErrors)];
            // store the errored field in the missing fields
            return { fields: uniqueFieldsWithErrors !== null && uniqueFieldsWithErrors !== void 0 ? uniqueFieldsWithErrors : [], count: (_b = uniqueFieldsWithErrors === null || uniqueFieldsWithErrors === void 0 ? void 0 : uniqueFieldsWithErrors.length) !== null && _b !== void 0 ? _b : 0 };
        }
        return { [lineItem.packageID]: [], count: 0 };
    });
    const totalCount = missingFields.reduce((acc, curr) => acc + curr.count, 0);
    return { missingFields, totalCount };
}
function lowConfidenceFieldsInLineItems(lineItems) {
    const lowConfidenceFields = lineItems.map((lineItem) => {
        return lineItem.confidence;
    }).map((confidence) => {
        const lowConfidenceFieldsFiltered = Object.keys(confidence).filter((key) => confidence[key] < LOW_CONFIDENCE_THRESHOLD);
        return lowConfidenceFieldsFiltered.reduce((acc, curr) => {
            acc[curr] = confidence[curr];
            return acc;
        }, {});
    });
    const errorFields = lowConfidenceFields.reduce((acc, curr, index) => {
        // update to 0 if the value of curr is undefined
        Object.keys(curr).forEach((key) => {
            if (curr[key] === undefined) {
                curr[key] = 0;
            }
        });
        acc[index] = curr;
        return acc;
    }, {});
    const totalCount = Object.keys(errorFields).map((key) => Object.keys(errorFields[key]).length).reduce((acc, curr) => acc + curr, 0);
    return { errorFields, totalCount };
}
function constructLineItems(sourceLineItems, originalInvoice) {
    var _a, _b, _c, _d, _e, _f;
    const destinationLineItems = [];
    for (let i = 0; i < sourceLineItems.length; i++) {
        const originalLineItem = originalInvoice.lineItems[i];
        const newLineItem = getEmptyLineItem(originalLineItem.type);
        newLineItem.defaultLineItem = Object.assign({}, sourceLineItems[i]);
        newLineItem.primaryItemData = sourceLineItems[i];
        newLineItem.secondaryItemData = sourceLineItems[i];
        newLineItem.isRemoved = sourceLineItems[i].removedFromOcrInvoice;
        newLineItem.matchResult = "equal";
        newLineItem.confidence = {
            name: (_a = originalLineItem.description) === null || _a === void 0 ? void 0 : _a.confidence,
            sku: (_b = originalLineItem.productCode) === null || _b === void 0 ? void 0 : _b.confidence,
            unit: (_c = originalLineItem.unit) === null || _c === void 0 ? void 0 : _c.confidence,
            quantity: (_d = originalLineItem.quantity) === null || _d === void 0 ? void 0 : _d.confidence,
            unitPrice: (_e = originalLineItem.unitPrice) === null || _e === void 0 ? void 0 : _e.confidence,
            weight: (_f = originalLineItem.weight) === null || _f === void 0 ? void 0 : _f.confidence,
        };
        newLineItem.type = originalLineItem.type;
        destinationLineItems.push(newLineItem);
    }
    ;
    return destinationLineItems;
}
function constructChartOfAccountDetails(sourceLineItems, originalInvoice) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const destinationLineItems = [];
    for (let i = 0; i < sourceLineItems.length; i++) {
        const originalLineItem = originalInvoice === null || originalInvoice === void 0 ? void 0 : originalInvoice.lineItems[i];
        const newLineItem = getEmptyLineItem((_a = originalLineItem === null || originalLineItem === void 0 ? void 0 : originalLineItem.type) !== null && _a !== void 0 ? _a : "product");
        newLineItem.defaultLineItem = Object.assign({}, sourceLineItems[i]);
        newLineItem.primaryItemData = sourceLineItems[i];
        newLineItem.secondaryItemData = sourceLineItems[i];
        newLineItem.isRemoved = sourceLineItems[i].removedFromOcrInvoice;
        newLineItem.matchResult = "equal";
        newLineItem.confidence = {
            name: (_b = originalLineItem === null || originalLineItem === void 0 ? void 0 : originalLineItem.description) === null || _b === void 0 ? void 0 : _b.confidence,
            sku: (_c = originalLineItem === null || originalLineItem === void 0 ? void 0 : originalLineItem.productCode) === null || _c === void 0 ? void 0 : _c.confidence,
            unit: (_d = originalLineItem === null || originalLineItem === void 0 ? void 0 : originalLineItem.unit) === null || _d === void 0 ? void 0 : _d.confidence,
            quantity: (_e = originalLineItem === null || originalLineItem === void 0 ? void 0 : originalLineItem.quantity) === null || _e === void 0 ? void 0 : _e.confidence,
            unitPrice: (_f = originalLineItem === null || originalLineItem === void 0 ? void 0 : originalLineItem.unitPrice) === null || _f === void 0 ? void 0 : _f.confidence,
            weight: (_g = originalLineItem === null || originalLineItem === void 0 ? void 0 : originalLineItem.weight) === null || _g === void 0 ? void 0 : _g.confidence,
        };
        newLineItem.type = (_h = originalLineItem === null || originalLineItem === void 0 ? void 0 : originalLineItem.type) !== null && _h !== void 0 ? _h : "product";
        destinationLineItems.push(newLineItem);
    }
    ;
    return destinationLineItems;
}
function transformNetworkData(data) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const ocrInvoice = getEmptyInvoice();
    const invoiceIDs = Object.keys(data.invoiceData.ocrParsedInvoiceData);
    const firstInvoiceID = invoiceIDs[0];
    const firstParsedInvoiceData = data.invoiceData.ocrParsedInvoiceData[firstInvoiceID];
    const lineItems = getLineItemsFromInvoiceData(firstParsedInvoiceData.lineItems);
    const chartOfAccountDetails = getLineItemsFromInvoiceData(firstParsedInvoiceData.chartOfAccountDetails);
    ocrInvoice.lineItems = constructLineItems(lineItems, firstParsedInvoiceData);
    ocrInvoice.chartOfAccountDetails = constructChartOfAccountDetails(chartOfAccountDetails, firstParsedInvoiceData);
    ocrInvoice.topLevelData = {
        invoiceNumber: firstParsedInvoiceData.purchaseOrderID.value,
        supplierName: data.supplierName,
        externalReferenceID: firstParsedInvoiceData.purchaseOrderID.value,
        buyerName: data.buyerName,
        issueDate: parseDateToIsoString((_a = firstParsedInvoiceData.invoiceDate) === null || _a === void 0 ? void 0 : _a.value) || getCurrentLocalDateAsUtcIso(),
        dueDate: parseDateToIsoString((_b = firstParsedInvoiceData === null || firstParsedInvoiceData === void 0 ? void 0 : firstParsedInvoiceData.deliveryDate) === null || _b === void 0 ? void 0 : _b.value) || getCurrentLocalDateAsUtcIso(),
        totalDiscount: valueToFixedNumber((_c = firstParsedInvoiceData === null || firstParsedInvoiceData === void 0 ? void 0 : firstParsedInvoiceData.totalDiscount) === null || _c === void 0 ? void 0 : _c.value),
        subtotal: (_d = firstParsedInvoiceData.netAmount) === null || _d === void 0 ? void 0 : _d.value,
        tax: (_e = firstParsedInvoiceData.totalTaxAmount) === null || _e === void 0 ? void 0 : _e.value,
        total: (_f = firstParsedInvoiceData.invoiceTotal) === null || _f === void 0 ? void 0 : _f.value,
        accountingClassID: firstParsedInvoiceData.qboClassID,
        accountingVendorID: firstParsedInvoiceData.qboVendorID,
        glCodeID: firstParsedInvoiceData.glCodeID,
        taxCalculationSettings: firstParsedInvoiceData.taxCalculationSettings,
        confidence: {
            invoiceNumber: firstParsedInvoiceData.purchaseOrderID.confidence,
            externalReferenceID: firstParsedInvoiceData.purchaseOrderID.confidence,
            issueDate: (_g = firstParsedInvoiceData.invoiceDate) === null || _g === void 0 ? void 0 : _g.confidence,
            dueDate: (_h = firstParsedInvoiceData === null || firstParsedInvoiceData === void 0 ? void 0 : firstParsedInvoiceData.deliveryDate) === null || _h === void 0 ? void 0 : _h.confidence,
            totalDiscount: (_j = firstParsedInvoiceData === null || firstParsedInvoiceData === void 0 ? void 0 : firstParsedInvoiceData.totalDiscount) === null || _j === void 0 ? void 0 : _j.confidence,
            subtotal: (_k = firstParsedInvoiceData.netAmount) === null || _k === void 0 ? void 0 : _k.confidence,
            tax: (_l = firstParsedInvoiceData.totalTaxAmount) === null || _l === void 0 ? void 0 : _l.confidence,
            total: (_m = firstParsedInvoiceData.invoiceTotal) === null || _m === void 0 ? void 0 : _m.confidence,
        }
    };
    ocrInvoice.isQBOEnabled = data.isAccountingEnabled;
    ocrInvoice.catalogID = data === null || data === void 0 ? void 0 : data.catalogID;
    ocrInvoice.catalogPriceListID = data === null || data === void 0 ? void 0 : data.catalogPriceListID;
    return Object.assign(Object.assign({}, data), ocrInvoice);
}
/**
 *
 */
async function getOcrCatalogData(buyerKey) {
    return axios.get(`${OCR_API_ENDPOINTS.SECRET_SHOP_API}/buyers/${buyerKey}/supplierCatalogs`).then((r) => r.data);
}
function parseCatalogData(catalogData, supplierUrlsafeKey) {
    if (catalogData) {
        for (let i = 0; i < catalogData.length; i++) {
            if (catalogData[i].supplierUrlsafeKey === supplierUrlsafeKey) {
                const foundCat = catalogData[i];
                if (foundCat) {
                    return {
                        catalogID: foundCat.id,
                        priceListID: foundCat.priceListID
                    };
                }
            }
        }
    }
    return null;
}
async function fetchPackageForLineItems(items, catalogID, priceListID) {
    const newLineItems = [...items];
    const promises = items
        .filter((lineItem) => !!lineItem.primaryItemData.sku)
        .map((lineItem) => getCatalogProductForSku(lineItem.primaryItemData.sku, catalogID, priceListID));
    const units = await (await fetchUnits()).units;
    const promiseResults = await Promise.allSettled(promises);
    // this variable generates a map with the sku as the key and the product as the value for all the products that were found in the catalog
    const catalogLineItemsMap = promiseResults === null || promiseResults === void 0 ? void 0 : promiseResults.reduce((map, result) => {
        if ((result === null || result === void 0 ? void 0 : result.status) === 'fulfilled') {
            const lineItem = result.value;
            if (lineItem === null || lineItem === void 0 ? void 0 : lineItem.sku) {
                map[lineItem.sku] = lineItem;
            }
        }
        return map;
    }, {});
    // loops over the line items and sets their linkedCatalogData if it exists in the map
    newLineItems === null || newLineItems === void 0 ? void 0 : newLineItems.forEach((lineItem) => {
        var _a;
        if ((_a = lineItem === null || lineItem === void 0 ? void 0 : lineItem.primaryItemData) === null || _a === void 0 ? void 0 : _a.sku) {
            const product = catalogLineItemsMap[lineItem.primaryItemData.sku];
            if (product) {
                const unit = getValidUnit(product.unit, units);
                lineItem.linkedCatalogData = {
                    id: product === null || product === void 0 ? void 0 : product.id,
                    description: product === null || product === void 0 ? void 0 : product.description,
                    availability: product === null || product === void 0 ? void 0 : product.availability,
                    sku: product === null || product === void 0 ? void 0 : product.sku,
                    quantity: product === null || product === void 0 ? void 0 : product.quantity,
                    unit,
                    prices: [...product.prices],
                    taxCode: product.taxCode,
                };
                // also set their units
                if (unit) {
                    lineItem.primaryItemData.unit = unit;
                }
                if (product.taxCode) {
                    if (product.taxCode === 'Fully Taxable') {
                        lineItem.primaryItemData.taxCode = '';
                    }
                    else {
                        lineItem.primaryItemData.taxCode = product.taxCode;
                    }
                }
                if (product.taxCode === TAX_CODE_OTHER_EXEMPT.value && !lineItem.primaryItemData.tax) {
                    lineItem.primaryItemData.tax = { dollarAmount: 0, percent: 0 };
                }
                if (!lineItem.primaryItemData.packageID && product) {
                    lineItem.primaryItemData.packageID = product === null || product === void 0 ? void 0 : product.id;
                }
            }
        }
    });
    return newLineItems;
}
/**
 * Fetch all possible product categories
 * @param supplierCatalogId
 * @param queryParams
 */
export async function fetchUnits() {
    return (await axios.get(`${OCR_API_ENDPOINTS.SECRET_SHOP_API}/units`)).data;
}
//# sourceMappingURL=OcrLoadNetwork.js.map